import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { ProductInventoryDto, ProductModel, ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import { useCollectiveProductContext } from "@toolkit/Framework/Components/ProductContext/CollectiveProductContext";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

export interface GetProductContextProps {
    loadPrice?: boolean;
    loadInventory?: boolean;
}

interface OwnProps {
    product: ProductModel;
}

type Props = OwnProps & GetProductContextProps & ResolveThunks<typeof mapDispatchToProps>;

const mapDispatchToProps = {
    loadRealTimePricing,
    loadRealTimeInventory,
};

const StoreProductContext: FC<Props> = ({
    product,
    loadPrice = true,
    loadInventory = true,
    children,
    loadRealTimePricing,
    loadRealTimeInventory,
}) => {
    const collectiveProductContext = useCollectiveProductContext();
    const contextData = collectiveProductContext.find(x => x.product.id === product?.id);
    const productInfo = contextData?.productInfo || createFromProduct(product);

    const [qtyOrdered, setQtyOrdered] = useState<number>(productInfo.qtyOrdered);
    const [unitOfMeasure, setUnitOfMeasure] = useState<string>(productInfo.unitOfMeasure);
    const [pricing, setPricing] = useState<ProductPriceDto | undefined>(productInfo.pricing);
    const [failedToLoadPricing, setFailedToLoadPricing] = useState<true | undefined>(productInfo.failedToLoadPricing);
    const [inventory, setInventory] = useState<ProductInventoryDto | undefined>(productInfo.inventory);

    const _getProductInfo = () =>
        ({
            ...productInfo,
            qtyOrdered,
            unitOfMeasure,
            pricing,
            failedToLoadPricing,
            inventory,
        } as ProductInfo);

    const _loadRealTimePricing = (data?: Partial<ProductInfo>) => {
        if (loadPrice) {
            loadRealTimePricing({
                productPriceParameters: [{ ..._getProductInfo(), ...data }],
                onSuccess: result => {
                    const pricing = result.realTimePricingResults?.[0];
                    if (pricing) {
                        setPricing(pricing);
                    } else {
                        setFailedToLoadPricing(true);
                    }
                },
                onError: () => {
                    setFailedToLoadPricing(true);
                },
            });
        }
    };

    const _loadRealTimeInventory = () => {
        if (loadInventory) {
            loadRealTimeInventory({
                productIds: [product.id],
                onSuccess: result => {
                    setInventory(result.realTimeInventoryResults?.[0]);
                },
            });
        }
    };

    useEffect(() => {
        if (!contextData) {
            _loadRealTimePricing();
            _loadRealTimeInventory();
        }
    }, []);

    useEffect(() => {
        if (productInfo.pricing && !pricing) {
            setPricing(productInfo.pricing);
        }

        if (productInfo.inventory && !inventory) {
            setInventory(productInfo.inventory);
        }

        if (productInfo.failedToLoadPricing && !failedToLoadPricing) {
            setFailedToLoadPricing(productInfo.failedToLoadPricing);
        }
    }, [productInfo]);

    const productContext: ProductContextModel = {
        product,
        productInfo: _getProductInfo(),
        onQtyOrderedChanged: setQtyOrdered,
        onUnitOfMeasureChanged: setUnitOfMeasure,
    };

    return <ProductContext.Provider value={productContext}>{children}</ProductContext.Provider>;
};

export default connect(null, mapDispatchToProps)(StoreProductContext);
